import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle, Hand, FileCheck } from 'lucide-react';

interface ExpandedRowContentProps {
  item: any;
  onReparse?: () => void;
  onAccept?: () => void;
  onManualReview?: () => void;
}

export function ExpandedRowContent({ item, onReparse, onAccept, onManualReview }: ExpandedRowContentProps) {
  const extractedData = item.extracted_data || {};
  
  const fields = [
    { name: 'Account Number', value: extractedData.account_number, confidence: item.account_confidence },
    { name: 'Bill Date', value: extractedData.bill_date, confidence: extractedData.confidence_scores?.bill_date },
    { name: 'Amount', value: extractedData.amount, confidence: extractedData.confidence_scores?.amount },
    { name: 'Meter Number', value: extractedData.meter_number, confidence: extractedData.confidence_scores?.meter_number },
    { name: 'Customer Name', value: extractedData.customer_name, confidence: item.customer_confidence },
    { name: 'Service Type', value: item.service_type, confidence: extractedData.confidence_scores?.service_type },
  ].filter(f => f.value !== undefined && f.value !== null);

  const getConfidenceStatus = (conf: number | undefined) => {
    if (!conf) return { icon: AlertTriangle, variant: 'outline' as const, text: 'N/A' };
    if (conf >= 0.90) return { icon: CheckCircle, variant: 'default' as const, text: '✅' };
    if (conf >= 0.70) return { icon: AlertTriangle, variant: 'secondary' as const, text: '⚠️' };
    return { icon: AlertTriangle, variant: 'destructive' as const, text: '❌' };
  };

  return (
    <div className="p-4 bg-muted/30 space-y-4">
      {/* Parsed Fields Table */}
      <div>
        <h4 className="font-semibold mb-2 text-sm">Extracted Fields</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Field Name</TableHead>
              <TableHead>Extracted Value</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, idx) => {
              const status = getConfidenceStatus(field.confidence);
              return (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{field.name}</TableCell>
                  <TableCell>{field.value}</TableCell>
                  <TableCell>
                    {field.confidence ? (
                      <Badge variant={status.variant}>
                        {(field.confidence * 100).toFixed(0)}%
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>{status.text}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Parsing Metadata */}
      {extractedData.ai_provider && (
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">AI Provider:</span>
            <p className="font-medium">{extractedData.ai_provider}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Processing Time:</span>
            <p className="font-medium">{item.processing_time_ms ? `${(item.processing_time_ms / 1000).toFixed(2)}s` : 'N/A'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Tokens Used:</span>
            <p className="font-medium">{extractedData.tokens_used || 'N/A'}</p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex items-center gap-2 pt-2 border-t">
        {onReparse && (
          <Button size="sm" variant="outline" onClick={onReparse}>
            Re-parse
          </Button>
        )}
        {onAccept && (
          <Button size="sm" variant="default" onClick={onAccept}>
            <FileCheck className="h-4 w-4 mr-2" />
            Accept
          </Button>
        )}
        {onManualReview && (
          <Button size="sm" variant="secondary" onClick={onManualReview}>
            <Hand className="h-4 w-4 mr-2" />
            Send to Manual Review
          </Button>
        )}
      </div>
    </div>
  );
}
